type SpectrogramRenderOptions = {
  gainMultiplier?: number;
  noiseGateDb?: number;
  minFreqHz?: number;
  maxFreqHz?: number;
};

const AUDACITY_COLOR_STOPS = [
  { t: 0.0, rgb: [0, 0, 0] },
  { t: 0.2, rgb: [20, 20, 90] },
  { t: 0.4, rgb: [20, 80, 220] },
  { t: 0.6, rgb: [0, 200, 255] },
  { t: 0.75, rgb: [240, 220, 50] },
  { t: 0.9, rgb: [250, 80, 30] },
  { t: 1.0, rgb: [255, 255, 255] },
];

export function audioBufferToWavBlob(audioBuffer: AudioBuffer): Blob {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;
  const bytesPerSample = 2;
  const blockAlign = numberOfChannels * bytesPerSample;
  const dataSize = samples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i += 1) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < samples; i += 1) {
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      const sample = audioBuffer.getChannelData(channel)[i];
      const clipped = Math.max(-1, Math.min(1, sample));
      const intSample = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

export async function decodeAudioData(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  const audioCtx = new AudioContextCtor();
  try {
    return await audioCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await audioCtx.close();
  }
}

export function buildTrimmedBuffer(
  sourceBuffer: AudioBuffer,
  trimStartMs: number,
  trimEndMs: number,
): AudioBuffer {
  const sampleRate = sourceBuffer.sampleRate;
  const startSample = Math.max(0, Math.floor((trimStartMs / 1000) * sampleRate));
  const endSample = Math.min(
    sourceBuffer.length,
    Math.floor(sourceBuffer.length - (trimEndMs / 1000) * sampleRate),
  );
  if (endSample <= startSample) {
    throw new Error('Trim values remove the whole clip. Reduce start/end trim.');
  }
  const trimmedLength = endSample - startSample;
  const trimmed = new AudioBuffer({
    length: trimmedLength,
    numberOfChannels: sourceBuffer.numberOfChannels,
    sampleRate: sourceBuffer.sampleRate,
  });
  for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel += 1) {
    const source = sourceBuffer.getChannelData(channel).subarray(startSample, endSample);
    trimmed.copyToChannel(source, channel);
  }
  return trimmed;
}

export function audacityColor(normalizedValue: number): { r: number; g: number; b: number } {
  const t = Math.max(0, Math.min(1, normalizedValue));
  for (let i = 1; i < AUDACITY_COLOR_STOPS.length; i += 1) {
    const a = AUDACITY_COLOR_STOPS[i - 1];
    const b = AUDACITY_COLOR_STOPS[i];
    if (t <= b.t) {
      const localT = (t - a.t) / (b.t - a.t || 1);
      const r = Math.round(a.rgb[0] + ((b.rgb[0] - a.rgb[0]) * localT));
      const g = Math.round(a.rgb[1] + ((b.rgb[1] - a.rgb[1]) * localT));
      const bColor = Math.round(a.rgb[2] + ((b.rgb[2] - a.rgb[2]) * localT));
      return { r, g, b: bColor };
    }
  }
  const last = AUDACITY_COLOR_STOPS[AUDACITY_COLOR_STOPS.length - 1].rgb;
  return { r: last[0], g: last[1], b: last[2] };
}

function nextPowerOfTwo(value: number): number {
  let n = 1;
  while (n < value) n <<= 1;
  return n;
}

function fftMagnitudes(realInput: Float32Array): Float32Array {
  const n = realInput.length;
  const real = new Float32Array(n);
  const imag = new Float32Array(n);
  real.set(realInput);

  let j = 0;
  for (let i = 1; i < n - 1; i += 1) {
    let bit = n >> 1;
    while (j & bit) {
      j ^= bit;
      bit >>= 1;
    }
    j ^= bit;
    if (i < j) {
      const tr = real[i];
      real[i] = real[j];
      real[j] = tr;
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const angle = (-2 * Math.PI) / len;
    const wLenCos = Math.cos(angle);
    const wLenSin = Math.sin(angle);
    for (let i = 0; i < n; i += len) {
      let wCos = 1;
      let wSin = 0;
      for (let k = 0; k < len / 2; k += 1) {
        const uReal = real[i + k];
        const uImag = imag[i + k];
        const vReal =
          (real[i + k + len / 2] * wCos) - (imag[i + k + len / 2] * wSin);
        const vImag =
          (real[i + k + len / 2] * wSin) + (imag[i + k + len / 2] * wCos);

        real[i + k] = uReal + vReal;
        imag[i + k] = uImag + vImag;
        real[i + k + len / 2] = uReal - vReal;
        imag[i + k + len / 2] = uImag - vImag;

        const nextCos = (wCos * wLenCos) - (wSin * wLenSin);
        const nextSin = (wCos * wLenSin) + (wSin * wLenCos);
        wCos = nextCos;
        wSin = nextSin;
      }
    }
  }

  const bins = n / 2;
  const magnitudes = new Float32Array(bins);
  for (let i = 0; i < bins; i += 1) {
    magnitudes[i] = Math.sqrt((real[i] ** 2) + (imag[i] ** 2));
  }
  return magnitudes;
}

export function renderSpectrogramToCanvas(
  audioBuffer: AudioBuffer,
  targetCanvas: HTMLCanvasElement,
  {
    gainMultiplier = 1,
    noiseGateDb = -70,
    minFreqHz = 0,
    maxFreqHz = audioBuffer.sampleRate / 2,
  }: SpectrogramRenderOptions = {},
): void {
  const channelData = audioBuffer.getChannelData(0);
  const fftSize = nextPowerOfTwo(2048);
  const hopSize = Math.floor(fftSize / 8);
  const frameCount = Math.max(1, Math.floor((channelData.length - fftSize) / hopSize));
  const width = Math.min(2400, frameCount);
  const frameStep = Math.max(1, Math.floor(frameCount / width));
  const height = Math.floor(fftSize / 2);

  targetCanvas.width = width;
  targetCanvas.height = height;
  const ctx = targetCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Unable to render spectrogram: 2d context not available.');
  }
  const imageData = ctx.createImageData(width, height);
  const windowFn = new Float32Array(fftSize);
  for (let i = 0; i < fftSize; i += 1) {
    windowFn[i] = 0.5 - (0.5 * Math.cos((2 * Math.PI * i) / (fftSize - 1)));
  }

  const frame = new Float32Array(fftSize);
  const dbFloor = -120;
  const dbCeil = -20;
  const gateLinear = 10 ** (noiseGateDb / 20);
  const nyquist = audioBuffer.sampleRate / 2;
  const clampedMinFreq = Math.max(0, Math.min(minFreqHz, nyquist));
  const clampedMaxFreq = Math.max(clampedMinFreq + 1, Math.min(maxFreqHz, nyquist));
  const minBin = Math.floor((clampedMinFreq / nyquist) * (height - 1));
  const maxBin = Math.max(minBin + 1, Math.floor((clampedMaxFreq / nyquist) * (height - 1)));
  for (let x = 0; x < width; x += 1) {
    const frameIndex = x * frameStep;
    const offset = frameIndex * hopSize;
    for (let i = 0; i < fftSize; i += 1) {
      const scaled = (channelData[offset + i] || 0) * gainMultiplier;
      const gated = Math.abs(scaled) >= gateLinear ? scaled : 0;
      const clipped = Math.max(-1, Math.min(1, gated));
      frame[i] = clipped * windowFn[i];
    }
    const magnitudes = fftMagnitudes(frame);
    for (let y = 0; y < height; y += 1) {
      const bin = height - 1 - y;
      if (bin < minBin || bin > maxBin) {
        const darkIndex = ((y * width) + x) * 4;
        imageData.data[darkIndex] = 0;
        imageData.data[darkIndex + 1] = 0;
        imageData.data[darkIndex + 2] = 0;
        imageData.data[darkIndex + 3] = 255;
        // eslint-disable-next-line no-continue
        continue;
      }
      const db = 20 * Math.log10((magnitudes[bin] || 1e-12) + 1e-12);
      const normalized = Math.max(0, Math.min(1, (db - dbFloor) / (dbCeil - dbFloor)));
      const { r, g, b } = audacityColor(normalized);
      const index = ((y * width) + x) * 4;
      imageData.data[index] = r;
      imageData.data[index + 1] = g;
      imageData.data[index + 2] = b;
      imageData.data[index + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);
}
